import { Link } from 'react-router-dom';
import type { CardView, CrossFitClass, CrossFitPayload } from '../api';
import { pickTraining } from '../briefing';
import {
  allBookings,
  captureBasisLabel,
  nextBooking,
  rosterState,
  rosterText,
  todayHeadline,
  todayState,
  waitlistText,
  workoutTeaser,
  type Booking,
} from '../crossfit';
import { formatDateTime, formatLocalDay, formatLocalDayShort, formatTime, formatWeekday, localDayParts, localToday } from '../format';
import { useOpenDetail } from './detail-link';
import { ChangeLine } from './editions';
import { Notice } from './ui';

function nowLocal(now = new Date()) {
  return { day: localToday(now), time: formatTime(now.toISOString()) };
}

function timeRange(c: { time_start: string; time_end?: string | null }) {
  return `${c.time_start}${c.time_end ? `–${c.time_end}` : ''}`;
}

function ownStatus(c: CrossFitClass) {
  if (c.own_status === 'reserved') return 'angemeldet';
  return c.own_waitlist_position ? `Warteliste, Platz ${c.own_waitlist_position}` : 'Warteliste';
}

/** Capture freshness: when the source was observed, as opposed to when it was collected. */
export function CaptureLine({ p }: { p: CrossFitPayload }) {
  const basis = captureBasisLabel(p.capture_basis);
  return (
    <p className="capture muted small">
      {p.captured_at ? (
        <>
          Erfasst {p.capture_approximate ? 'ca. ' : ''}
          {formatDateTime(p.captured_at)}
          {basis ? ` (Grundlage: ${basis})` : ''}
        </>
      ) : p.payload_type === 'crossfit.v2' ? (
        'Erfassungszeit unbekannt'
      ) : (
        'Gelesen vom Browser-Leser'
      )}
      {p.collected_at && <> · abgeholt {formatDateTime(p.collected_at)}</>}
      {/* A reading that did not read the workout or the bookings keeps the day's earlier reading of them. */}
      {p.workout_captured_at && p.workout_captured_at !== p.captured_at && <> · Workout aus der Lesung {formatTime(p.workout_captured_at)}</>}
      {p.bookings_captured_at && p.bookings_captured_at !== p.captured_at && <> · Buchungen aus der Lesung {formatTime(p.bookings_captured_at)}</>}
    </p>
  );
}

/**
 * The local day of the capture when it is earlier than the day the card describes:
 * the phone produced no new archive and the platform read an older one. The
 * reader must see that date; "partial" or "stale" alone says nothing about it.
 */
export function earlierCaptureDay(p: CrossFitPayload): string | null {
  if (!p.captured_at) return null;
  const day = localToday(new Date(p.captured_at));
  return day < p.local_date ? day : null;
}

export function StaleNotice({ p }: { p: CrossFitPayload }) {
  if (!p.stale) return null;
  const earlier = earlierCaptureDay(p);
  if (earlier) {
    return (
      <Notice kind="warn" title="Aufnahme von einem früheren Tag">
        Diese Angaben stammen aus der Aufnahme vom {formatLocalDayShort(earlier)}
        {p.captured_at ? ` (${formatDateTime(p.captured_at)})` : ''}. Für {formatLocalDayShort(p.local_date)} ist keine
        neuere Aufnahme eingetroffen.
      </Notice>
    );
  }
  return (
    <Notice kind="warn" title="Nicht mehr aktuell">
      Stand der letzten vollständigen Lesung
      {p.stale_since ? ` vom ${formatDateTime(p.stale_since)}` : ''}. Die neueste Aktualisierung war unvollständig.
    </Notice>
  );
}

function DateTile({ day }: { day: string }) {
  const parts = localDayParts(day);
  if (!parts) return null;
  return (
    <div className="calendar-date" aria-hidden="true">
      <small>{parts.month}</small>
      <b>{parts.day}</b>
    </div>
  );
}

/** The training card beside the topic board. */
export function TrainingPanel({ views, day }: { views: CardView[]; day: string }) {
  const open = useOpenDetail();
  const { primary: cv, others } = pickTraining(views, day);
  if (!cv) {
    return (
      <section className="panel day-panel" aria-labelledby="training-h">
        <span className="eyebrow">Dein Training</span>
        <h2 id="training-h">Keine Trainingsdaten in dieser Ausgabe.</h2>
        <p className="muted">Die Ausgabe enthält keine CrossFit-Karte. Das heisst nicht, dass du keine Anmeldung hast.</p>
        <Link className="text-button" to="/crossfit">
          CrossFit öffnen ↗
        </Link>
      </section>
    );
  }
  const p = cv.version.payload as CrossFitPayload;
  const state = todayState(p);
  const now = nowLocal();
  const next = nextBooking(p, now);
  return (
    <section className="panel day-panel" aria-labelledby="training-h">
      <span className="eyebrow">Dein Training · {formatLocalDayShort(p.local_date)}</span>
      <h2 id="training-h">{todayHeadline(state, p.local_date === now.day)}</h2>
      {/* What changed since the edition's baseline; a mere later reading says so. */}
      <ChangeLine view={cv} compact />
      {state.kind === 'unknown' && <p className="muted">{state.reason}</p>}
      {next ? (
        <div className="date-tile">
          <DateTile day={next.local_date} />
          <div>
            <p>
              {next.local_date === now.day ? 'Heute' : formatWeekday(next.local_date)} · {timeRange(next)}
            </p>
            <span>
              {next.class_type} · nächste Anmeldung{next.own_status === 'waitlisted' ? ' (Warteliste)' : ''}
            </span>
          </div>
        </div>
      ) : (
        <p className="muted">Keine weitere Anmeldung bekannt.</p>
      )}
      <div className="line">
        {p.workout ? (
          <p className="muted">Workout für {formatLocalDayShort(p.local_date)} liegt vor.</p>
        ) : (
          <p className="muted">Kein Workout erfasst.</p>
        )}
        {p.stale && (
          <p className="warn-text">
            {earlierCaptureDay(p)
              ? `Stand nicht aktuell: Aufnahme vom ${formatLocalDayShort(earlierCaptureDay(p)!)}.`
              : 'Stand nicht aktuell (letzte vollständige Lesung).'}
          </p>
        )}
        {p.collection_status === 'partial' && !p.stale && <p className="warn-text">Teilweise erfasst.</p>}
        <div className="panel-actions">
          <Link className="text-button" to="/crossfit">
            Workout &amp; Anmeldungen ↗
          </Link>
          <button type="button" className="text-button" onClick={(e) => open(cv.version.id, e.currentTarget)}>
            Details
          </button>
        </div>
      </div>
      {others.length > 0 && (
        <details className="line other-days">
          <summary>Weitere Trainingstage in dieser Ausgabe ({others.length})</summary>
          <ul className="plain">
            {others.map((o) => {
              const op = o.version.payload as CrossFitPayload;
              return (
                <li key={o.version.id}>
                  <button type="button" className="text-button" onClick={(e) => open(o.version.id, e.currentTarget)}>
                    {formatLocalDayShort(op.local_date)}: {todayHeadline(todayState(op), false)}
                  </button>
                </li>
              );
            })}
          </ul>
        </details>
      )}
    </section>
  );
}

function Roster({ c, showNames }: { c: CrossFitClass; showNames: boolean }) {
  const r = rosterState(c);
  const kind = r.kind === 'complete' || r.kind === 'empty' ? 'ok' : r.kind === 'partial' ? 'warn' : 'danger';
  return (
    <div className="roster-block">
      <p className={`roster-state roster-${kind}`}>
        <span className="visually-hidden">Teilnehmerliste: </span>
        {rosterText(c)} · {waitlistText(c)}
      </p>
      {showNames && c.participants && c.participants.length > 0 && (
        <ol className="roster" aria-label={`Teilnehmende ${timeRange(c)}`}>
          {c.participants.map((pp) => (
            <li key={`p-${pp.position}`}>
              <span className="pos">{pp.position}</span>
              <span>{pp.display_name}</span>
            </li>
          ))}
        </ol>
      )}
      {showNames && c.waitlist && c.waitlist.length > 0 && (
        <>
          <h4 className="mini-h">Warteliste</h4>
          <ol className="roster">
            {c.waitlist.map((pp) => (
              <li key={`w-${pp.position}`}>
                <span className="pos">{pp.position}</span>
                <span>{pp.display_name}</span>
              </li>
            ))}
          </ol>
        </>
      )}
      {r.kind === 'partial' && <p className="muted small">Unbekannt heisst nicht leer: die Liste wurde nur teilweise gelesen.</p>}
      {c.missing_fields && c.missing_fields.length > 0 && (
        <p className="muted small">Nicht gelesen: {c.missing_fields.join(', ')}</p>
      )}
    </div>
  );
}

function BookingItem({ b, showNames, next }: { b: Booking; showNames: boolean; next: boolean }) {
  return (
    <li className="booking">
      <strong>
        {/* The date is always shown: the risk with a future booking is reading it as today's. */}
        {b.local_date === localToday() ? 'Heute' : formatLocalDayShort(b.local_date)} · {timeRange(b)}
      </strong>
      <span>
        {b.class_type} · {ownStatus(b)}
        {b.coach ? ` · ${b.coach.toLowerCase().startsWith('coach ') ? b.coach : `Coach ${b.coach}`}` : ''}
        {next ? ' · nächste Anmeldung' : ''}
      </span>
      <Roster c={b} showNames={showNames} />
      {b.workout && (
        <details>
          <summary>Workout dieser Klasse</summary>
          <pre className="workout-text">{b.workout}</pre>
        </details>
      )}
    </li>
  );
}

/** The complete composition, used on the CrossFit page and in the details modal. */
export function CrossFitView({ p, headingLevel = 2 }: { p: CrossFitPayload; headingLevel?: 2 | 3 }) {
  const H = headingLevel === 2 ? 'h2' : 'h3';
  const state = todayState(p);
  const bookings = allBookings(p);
  const next = nextBooking(p, nowLocal());
  const schedule = p.schedule ?? null;

  return (
    <div className="crossfit">
      <StaleNotice p={p} />
      {p.collection_status === 'failed' && (
        <Notice kind="danger" title="Lesung fehlgeschlagen">
          Die Daten für {formatLocalDay(p.local_date)} konnten nicht gelesen werden.
        </Notice>
      )}
      {p.collection_status === 'partial' && !p.stale && (
        <Notice kind="warn" title="Teilweise erfasst">
          Einige Angaben fehlen; was fehlt, steht bei der jeweiligen Klasse.
        </Notice>
      )}

      <div className="workout-grid">
        <article className="panel">
          <span className="eyebrow">Workout · {formatLocalDay(p.local_date)}</span>
          {p.workout ? (
            <>
              <H className="panel-title">{p.workout.title || 'Workout des Tages'}</H>
              {p.workout.displayed_date_text && p.workout.displayed_date_text !== p.workout.title && (
                <p className="muted small">In der App angezeigt als: {p.workout.displayed_date_text}</p>
              )}
              {/* Original text: whitespace, units, reps and order exactly as captured. */}
              <pre className="workout-text" lang="en">
                {p.workout.body}
              </pre>
            </>
          ) : (
            <>
              <H className="panel-title">Kein Workout erfasst.</H>
              <p className="muted">
                {p.payload_type === 'crossfit.v1'
                  ? 'Der Browser-Leser erfasst kein Tagesworkout.'
                  : 'Für diesen Tag wurde kein Workout gelesen.'}
              </p>
            </>
          )}
          <details>
            <summary>Herkunft &amp; Erfassungsstand</summary>
            <CaptureLine p={p} />
            <p className="muted small">
              Das Datum des Workouts gehört zum Workout selbst ({p.local_date}); spätere Buchungen tragen ihr eigenes Datum.
            </p>
          </details>
        </article>

        <div className="stack">
          <section className="panel" aria-label="Deine Anmeldungen">
            <span className="eyebrow">Deine Anmeldungen</span>
            <H className="panel-title">
              {p.local_date === localToday() ? todayHeadline(state) : `${formatLocalDayShort(p.local_date)}: ${todayHeadline(state, false)}`}
            </H>
            {state.kind === 'unknown' && <p className="muted">{state.reason}</p>}
            {state.kind === 'booked' && state.fromSchedule && (
              <p className="muted">Die Buchungsliste wurde bei dieser Lesung nicht gelesen; der Kursplan derselben Lesung zeigt die Anmeldung.</p>
            )}
            {state.kind === 'none' && <p className="muted">Kein Fehler: für diesen Tag ist keine Klasse gebucht.</p>}
            {bookings.length > 0 ? (
              <ul className="bookings">
                {bookings.map((b) => (
                  <BookingItem
                    key={`${b.local_date}-${b.class_key}`}
                    b={b}
                    showNames
                    next={next !== null && next.local_date === b.local_date && next.class_key === b.class_key}
                  />
                ))}
              </ul>
            ) : p.upcoming === null || (p.upcoming === undefined && p.payload_type === 'crossfit.v2') ? (
              <p className="muted small">Spätere Buchungen wurden nicht gelesen.</p>
            ) : (
              <p className="muted small">Keine späteren Buchungen bekannt.</p>
            )}
          </section>

          <section className="panel" aria-label="Kurszeiten">
            <span className="eyebrow">Kurszeiten · {formatLocalDayShort(p.local_date)}</span>
            {schedule === null || schedule === undefined ? (
              <p className="muted">
                {p.payload_type === 'crossfit.v1' ? 'Der Browser-Leser erfasst keinen Stundenplan.' : 'Der Stundenplan wurde nicht gelesen.'}
              </p>
            ) : schedule.length === 0 ? (
              <p className="muted">Stundenplan gelesen: keine Kurse an diesem Tag.</p>
            ) : (
              <ul className="schedule">
                {schedule.map((s) => {
                  const known = s.filled != null && s.capacity != null && s.capacity > 0;
                  const pct = known ? Math.min(100, Math.round(((s.filled as number) / (s.capacity as number)) * 100)) : 0;
                  return (
                    <li key={`${s.time_start}-${s.class_type}`} className={s.reserved ? 'is-reserved' : undefined}>
                      <span className="sched-time">{timeRange(s)}</span>
                      <span className="sched-type">
                        {s.class_type}
                        {s.reserved ? <strong> · angemeldet</strong> : ''}
                      </span>
                      <span className="sched-fill">
                        {known ? (
                          <>
                            <span className="fill-bar" aria-hidden="true">
                              <span style={{ width: `${pct}%` }} />
                            </span>
                            {s.filled}/{s.capacity} belegt
                          </>
                        ) : (
                          'Belegung unbekannt'
                        )}
                      </span>
                    </li>
                  );
                })}
              </ul>
            )}
          </section>
        </div>
      </div>

      {p.notes && p.notes.length > 0 && (
        <section className="panel notes">
          <span className="eyebrow">Hinweise der Lesung</span>
          <ul>
            {p.notes.map((n, i) => (
              <li key={i}>{n}</li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
}

export function workoutPreview(p: CrossFitPayload) {
  return p.workout ? workoutTeaser(p.workout.body) : null;
}
